import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle, WarningCircle, X } from '@phosphor-icons/react';

/** Plain-English meaning of the codes the OAuth callback sends back (oauth.controller.ts). */
const ERROR_TEXT: Record<string, string> = {
  access_denied: 'You cancelled the sign-in, so nothing was connected.',
  account_mismatch:
    'You signed in with a different account from the one being reconnected. Sign in with the same account and try again.',
  missing_scopes:
    'Not every permission was granted, so the account was not connected. Try again and allow all requested access — your organisation may need to approve the app.',
  missing_params: 'The sign-in did not complete. Please try again.',
  oauth_error: 'The account could not be connected. Please try again.',
};

export function oauthErrorText(code: string): string {
  return ERROR_TEXT[code] ?? ERROR_TEXT.oauth_error!;
}

const CONNECTED_LABEL: Record<string, string> = { gmail: 'Google account', microsoft: 'Microsoft account' };

/**
 * The outcome of an OAuth round trip, read from `?connected=` / `?error=` on the page the provider
 * returned to. It is captured BEFORE the address is tidied, so it stays on screen: the inbox used to
 * strip those parameters on its first render and the message vanished before anyone could read it.
 */
export function OAuthReturnNotice({ connectedText, floating = false }: {
  /** Added after "Google account connected." — what happens next on this page. */
  connectedText?: string;
  /** Pin over the page (inbox layout) rather than sitting in the flow. */
  floating?: boolean;
}) {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(() => {
    const error = params.get('error');
    if (error) return { ok: false, text: oauthErrorText(error) };
    const connected = params.get('connected');
    if (connected) {
      return { ok: true, text: `${CONNECTED_LABEL[connected] ?? 'Account'} connected.${connectedText ? ` ${connectedText}` : ''}` };
    }
    return null;
  });

  useEffect(() => {
    if (!params.has('connected') && !params.has('error')) return;
    const next = new URLSearchParams(params);
    next.delete('connected');
    next.delete('error');
    setParams(next, { replace: true });
    // The connection changed on the server — every view of it refetches.
    for (const key of [['mailboxes'], ['mailbox-health'], ['folder-counts'], ['calendar-sync-status'], ['email-sync-health']]) {
      void qc.invalidateQueries({ queryKey: key });
    }
    // Runs once, on arrival from the provider.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!notice) return null;

  return (
    <div
      role={notice.ok ? 'status' : 'alert'}
      data-testid="oauth-return-notice"
      className={`flex items-start gap-2.5 rounded-lg border px-4 py-3 text-sm ${
        notice.ok
          ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200'
          : 'border-red-200 bg-red-50 text-red-900 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200'
      } ${floating ? 'fixed left-1/2 top-[72px] z-[100] w-[min(560px,90vw)] -translate-x-1/2 shadow-lg' : 'mb-4'}`}
    >
      {notice.ok
        ? <CheckCircle size={16} weight="fill" className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
        : <WarningCircle size={16} weight="fill" className="mt-0.5 shrink-0 text-red-600 dark:text-red-400" />}
      <span className="flex-1">{notice.text}</span>
      <button type="button" aria-label="Dismiss" onClick={() => setNotice(null)} className="shrink-0 opacity-60 hover:opacity-100">
        <X size={14} />
      </button>
    </div>
  );
}
